import { BookOpenIcon, LockIcon } from "lucide-react";
import { useState } from "react";

import { PaywallDialog } from "@/components/PaywallDialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsPlusQuery, useWordSetsQuery } from "@/hooks/queries";
import { useActiveWordSetIdQuery, useSetActiveWordSetMutation } from "@/hooks/queries/useWordSetSettingsQuery";
import { trackEvent } from "@/lib/analytics";

export function CategorySettingsSection() {
  const [paywallOpen, setPaywallOpen] = useState(false);
  const { data: wordSets = [], isPending: isWordSetsPending } = useWordSetsQuery();
  const { data: isPlus = false } = useIsPlusQuery();
  const { data: activeSetId, isPending: isSettingsPending } = useActiveWordSetIdQuery();
  const { mutate: setActiveSet } = useSetActiveWordSetMutation();

  const isPending = isWordSetsPending || isSettingsPending;

  const selectableSets = isPlus ? wordSets : wordSets.filter(s => s.is_free);
  const lockedSets = isPlus ? [] : wordSets.filter(s => !s.is_free);

  // 기본값: 설정이 없으면 첫 번째 선택 가능한 세트
  const currentValue = activeSetId ?? selectableSets[0]?.id ?? "";
  const currentTitle = wordSets.find(s => s.id === currentValue)?.title;

  const handleChange = (setId: string | null) => {
    if (setId == null || (!isPlus && lockedSets.some(s => s.id === setId))) {
      return;
    }

    const selectedTitle = wordSets.find(s => s.id === setId)?.title;
    trackEvent("active_word_set_changed", { word_set_id: setId, title: selectedTitle });
    setActiveSet({ setId, allSetIds: wordSets.map(s => s.id) });
  };

  return (
    <section>
      <div className="rounded-xl bg-card overflow-hidden">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <BookOpenIcon className="size-4 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">출제 범위</p>
              <p className="text-xs text-muted-foreground">테스트에 출제할 단어장</p>
            </div>
          </div>

          {isPending ? (
            <Skeleton className="h-8 w-24 rounded-md" />
          ) : (
            <Select value={currentValue} onValueChange={handleChange}>
              <SelectTrigger size="sm">
                <SelectValue placeholder="단어장 선택">{currentTitle}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {selectableSets.map(set => (
                  <SelectItem key={set.id} value={set.id}>
                    {set.title}
                  </SelectItem>
                ))}
                {lockedSets.map(set => (
                  <SelectItem
                    key={set.id}
                    value={set.id}
                    onClick={() => {
                      trackEvent("paywall_triggered", { source: "category_settings", word_set_id: set.id });
                      setPaywallOpen(true);
                    }}
                  >
                    <span className="flex items-center gap-1.5 opacity-50">
                      {set.title}
                      <Badge className="text-xs px-1.5 py-0 gap-0.5 text-primary-foreground">
                        <LockIcon className="size-2.5 text-primary-foreground" />
                        Plus
                      </Badge>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      <PaywallDialog open={paywallOpen} onOpenChange={setPaywallOpen} source="category_settings" />
    </section>
  );
}
